import React from 'react';
import { CgSpinner } from 'react-icons/cg';
import { TbDownload } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import { formatDate } from '../../../utils/stringUtils';
import Button from '../buttons/Button';
import { InvoiceData } from '../../../types/payments';
import { downloadInvoice } from '../../../utils/subscriptionUtils';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableHelperText,
  BaseTableRowSkeleton,
  BaseTableSpacerRow,
} from './BaseTable';

type InvoiceTableProps = { invoices: InvoiceData[]; loading: boolean };

export default function InvoicesTable({ invoices, loading }: InvoiceTableProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.settings.invoices' });
  const { t: manageT } = useTranslation('', { keyPrefix: 'page.settings.manageSubscription' });
  const [idsDownloading, setIdsDownloading] = React.useState<Set<string>>(new Set());

  const handleDownload = async (id: string) => {
    setIdsDownloading((prev) => new Set(prev).add(id));
    downloadInvoice(id).finally(() =>
      setIdsDownloading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      }),
    );
  };

  return (
    <div>
      <BaseTable>
        <BaseTableHeader>
          <BaseTableHeaderRow>
            <HeaderCell>{manageT('lastPayments')}</HeaderCell>
            <HeaderCell>{manageT('total')}</HeaderCell>
            <HeaderCell>{manageT('status')}</HeaderCell>
          </BaseTableHeaderRow>
        </BaseTableHeader>
        <BaseTableBody>
          <BaseTableHelperText>{t('helper')}</BaseTableHelperText>
          <BaseTableSpacerRow />
          {loading && <BaseTableRowSkeleton />}
          {!loading &&
            invoices.map((invoice) => (
              <BaseTableDataRow key={invoice.id}>
                <DataCell>{formatDate(new Date(invoice.date * 1000))}</DataCell>
                <DataCell>&euro;{(invoice.amount / 100).toFixed(2)}</DataCell>
                <DataCell>
                  <div className="flex items-center">
                    <p className="uppercase">{invoice.status}</p>
                    <Button
                      className="hover:opacity-75 disabled:opacity-75"
                      disabled={idsDownloading.has(invoice.id)}
                      onClick={() => handleDownload(invoice.id)}>
                      {idsDownloading.has(invoice.id) ? (
                        <CgSpinner size={20} className="animate-spin" />
                      ) : (
                        <TbDownload size={20} />
                      )}
                    </Button>
                  </div>
                </DataCell>
              </BaseTableDataRow>
            ))}
        </BaseTableBody>
      </BaseTable>
    </div>
  );
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 !bg-primary-900 text-secondary-50', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td className={twMerge('whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900', className)} {...props} />
  );
}
